/**
 * RED-317 PR 3d-A — a chart's data as the canvas edits it, and the override
 * the rawBlock carries (attr `chartEdit`, contract §7). The override is the
 * difference to the chart part the block was imported with, minimal: only
 * the cells, labels, names and texts that differ, plus — once rows or series
 * were added or removed — which base row and series each target one is.
 *
 *   {
 *     v: 1,
 *     rows?:   [[from, to] | null, …]   target rows: runs of base rows, null = a new row
 *     series?: [{ idx, like? }, …]      target series in display order; like = the neighbour a new one copies
 *     cells?:  { [row]: { [idx]: number|null } }   values by target row and series idx
 *     cats?:   { [row]: { [level]: string|number|null } }  category labels (level 0 innermost; numbers for date/number categories)
 *     names?:  { [idx]: string }        series names
 *     text?:   { [key]: string }        'title', 'axis:<id>', 'shape:<a>.<s>': the plain text, paragraphs by newlines
 *   }
 *
 * The compiler always applies it to that same base part
 * (App\Services\Docx\Studio\Compile\Charts\ChartTable mirrors applyEdit()),
 * so undo across autosaves needs nothing more. Every operation here is pure:
 * it returns a new override (null when nothing differs any more).
 */

export const EDIT_VERSION = 1

/**
 * The most points an edited chart may declare: what the server reads back
 * (ChartReader::MAX_POINTS), and past it refuses the autosave.
 */
export const MAX_POINTS = 10000

/**
 * The points a sheet of `rows` × `series` declares as Word caches it: each
 * series its values and, on a chart with categories, its categories, one
 * point per row each. Mirrors ChartTable::points().
 */
export const chartPoints = (rows, series, kind) => rows * series * (kind === 'none' ? 1 : 2)

/** Whether a target sheet stays within MAX_POINTS; a chart already past it keeps its size, never grows. */
export const fitsPoints = (base, rows, series) => chartPoints(rows, series, base.kind) <= Math.max(MAX_POINTS, chartPoints(base.rows, base.series.length, base.kind))

/** The base sheet of a chart model (its server-read `data`). */
export function baseTable(model) {
  const data = model?.data
  if (!data) return null
  const categories = data.categories ?? { kind: 'none', levels: [], numbers: null, count: 0 }
  return {
    rows: data.rows ?? 0,
    kind: categories.kind ?? 'none',
    levels: (categories.levels ?? []).map((level) => [...level]),
    numbers: categories.numbers ? [...categories.numbers] : null,
    count: categories.count ?? 0,
    display: categories.display ?? categories.formatCode ?? 'General',
    formatCode: categories.formatCode ?? 'General',
    workbook: data.workbook ?? 'none',
    series: (data.series ?? []).map((series) => ({ ...series, values: [...series.values] })),
  }
}

/** Target row → base row (null = new) from the override's runs. */
export function expandRows(runs, baseRows) {
  if (!runs) return Array.from({ length: baseRows }, (_, index) => index)
  const out = []
  for (const run of runs) {
    if (run === null) out.push(null)
    else for (let row = run[0]; row <= run[1]; row++) out.push(row)
  }
  return out
}

/** Target row map → runs; null when it is the base's own rows. */
export function compressRows(map, baseRows) {
  if (map.length === baseRows && map.every((row, index) => row === index)) return null
  const runs = []
  for (const row of map) {
    const last = runs[runs.length - 1]
    if (row === null) runs.push(null)
    else if (last && last !== null && last[1] === row - 1) last[1] = row
    else runs.push([row, row])
  }
  return runs
}

const seriesList = (base, edit) => edit?.series ?? base.series.map((series) => ({ idx: series.idx }))

/** The base series a target series copies: itself, or for a new one its neighbour (`like`). */
function sourceOf(base, entry) {
  return base.series.find((series) => series.idx === entry.idx) ?? null
}

function likeOf(base, entry, list) {
  let like = entry.like
  for (let guard = 0; guard < 300 && like !== undefined; guard++) {
    const found = base.series.find((series) => series.idx === like)
    if (found) return found
    like = list.find((other) => other.idx === like)?.like
  }
  return base.series[0] ?? null
}

/**
 * The target sheet: base ⊕ override.
 * @returns {{ rows, rowMap, kind, levels, numbers, display, formatCode, workbook, categoriesChanged, structural,
 *             series: { idx, isNew, like, group, type, name, values, formatCode, categoryCount }[] }}
 */
export function applyEdit(base, edit) {
  const rowMap = expandRows(edit?.rows ?? null, base.rows)
  const rows = rowMap.length
  const structural = Boolean(edit?.rows) || Boolean(edit?.series)
  const list = seriesList(base, edit)
  const series = list.map((entry) => {
    const own = sourceOf(base, entry)
    const like = own ?? likeOf(base, entry, list)
    let values
    if (own && !edit?.rows) values = [...own.values]
    else values = rowMap.map((row) => (own && row !== null ? (own.values[row] ?? null) : null))
    for (const [row, cells] of Object.entries(edit?.cells ?? {})) {
      const index = Number(row)
      if (!(entry.idx in cells) || index >= rows) continue
      while (values.length <= index) values.push(null)
      values[index] = cells[entry.idx]
    }
    return {
      idx: entry.idx,
      isNew: !own,
      like: own ? null : (like?.idx ?? null),
      group: like?.group ?? 0,
      type: like?.type ?? 'bar',
      name: edit?.names?.[entry.idx] ?? (own ? own.name : ''),
      values,
      formatCode: like?.formatCode ?? 'General',
      categoryCount: own ? own.categoryCount : (like?.categoryCount ?? null),
    }
  })
  const numeric = base.kind === 'number' || base.kind === 'date'
  // A new row's innermost label is empty text (a point of its own), its outer levels continue the group above.
  const levels = base.levels.map((level, depth) => rowMap.map((row) => (row === null ? (depth === 0 ? '' : null) : (level[row] ?? null))))
  const numbers = base.numbers ? rowMap.map((row) => (row === null ? null : (base.numbers[row] ?? null))) : null
  const cats = edit?.cats ?? {}
  for (const [row, labels] of Object.entries(cats)) {
    const index = Number(row)
    if (index >= rows) continue
    for (const [level, label] of Object.entries(labels)) {
      if (numeric && numbers) numbers[index] = normalLabel(base, 0, label)
      else if (levels[Number(level)]) levels[Number(level)][index] = normalLabel(base, Number(level), label)
    }
  }
  return {
    rows,
    rowMap,
    kind: base.kind,
    levels,
    numbers,
    display: base.display,
    formatCode: base.formatCode,
    workbook: base.workbook,
    categoriesChanged: Boolean(edit?.rows) || Object.keys(cats).length > 0,
    structural,
    series,
  }
}

// ------------------------------------------------------------ operations

const clone = (edit) => JSON.parse(JSON.stringify(edit ?? { v: EDIT_VERSION }))

/** Drop empty maps; null when the override says nothing any more. */
export function normalizeEdit(edit, base) {
  if (!edit) return null
  const out = { v: EDIT_VERSION }
  if (edit.rows) {
    const runs = compressRows(expandRows(edit.rows, base.rows), base.rows)
    if (runs) out.rows = runs
  }
  if (edit.series) {
    const same = edit.series.length === base.series.length && edit.series.every((entry, index) => entry.like === undefined && entry.idx === base.series[index].idx)
    if (!same) out.series = edit.series
  }
  for (const key of ['cells', 'cats']) {
    const map = {}
    for (const [row, entries] of Object.entries(edit[key] ?? {})) {
      if (Object.keys(entries).length) map[row] = entries
    }
    if (Object.keys(map).length) out[key] = map
  }
  for (const key of ['names', 'text']) {
    if (edit[key] && Object.keys(edit[key]).length) out[key] = edit[key]
  }
  return Object.keys(out).length > 1 ? out : null
}

/** The base value under target cell (row, idx), what a minimal override leaves out. */
function baseValue(base, edit, row, idx) {
  const map = expandRows(edit?.rows ?? null, base.rows)
  const own = base.series.find((series) => series.idx === idx)
  const baseRow = map[row]
  return own && baseRow !== null && baseRow !== undefined ? (own.values[baseRow] ?? null) : null
}

export function setCell(base, edit, row, idx, value) {
  const next = clone(edit)
  next.cells ??= {}
  next.cells[row] ??= {}
  if (value === baseValue(base, edit, row, idx)) delete next.cells[row][idx]
  else next.cells[row][idx] = value
  return normalizeEdit(next, base)
}

function baseLabel(base, edit, row, level) {
  const map = expandRows(edit?.rows ?? null, base.rows)
  const baseRow = map[row]
  const numeric = base.kind === 'number' || base.kind === 'date'
  if (baseRow === null || baseRow === undefined) return null
  return numeric ? (base.numbers?.[baseRow] ?? null) : (base.levels[level]?.[baseRow] ?? null)
}

/** A category label as stored: text levels keep '' innermost and null outside (a group's continuation). */
export function normalLabel(base, level, label) {
  if (base.kind === 'number' || base.kind === 'date') return label === null || label === undefined || label === '' ? null : Number(label)
  if (label === null || label === undefined || label === '') return level === 0 ? '' : null
  return String(label)
}

export function setCategory(base, edit, row, level, label) {
  const next = clone(edit)
  next.cats ??= {}
  next.cats[row] ??= {}
  const value = normalLabel(base, level, label)
  if (value === normalLabel(base, level, baseLabel(base, edit, row, level))) delete next.cats[row][level]
  else next.cats[row][level] = value
  return normalizeEdit(next, base)
}

export function setName(base, edit, idx, name) {
  const next = clone(edit)
  next.names ??= {}
  const own = base.series.find((series) => series.idx === idx)
  if (own && own.name === name) delete next.names[idx]
  else next.names[idx] = name
  return normalizeEdit(next, base)
}

export function setText(base, edit, key, text, baseText) {
  const next = clone(edit)
  next.text ??= {}
  if (text === baseText) delete next.text[key]
  else next.text[key] = text
  return normalizeEdit(next, base)
}

function shiftRows(map, at, delta) {
  const out = {}
  for (const [row, entries] of Object.entries(map ?? {})) {
    const index = Number(row)
    if (delta < 0 && index === at) continue
    out[index >= at + (delta < 0 ? 1 : 0) ? index + delta : index] = entries
  }
  return out
}

/** Whether `count` more rows fit (fitsPoints). */
export function canInsertRow(base, edit, count = 1) {
  return fitsPoints(base, expandRows(edit?.rows ?? null, base.rows).length + count, seriesList(base, edit).length)
}

/** A blank row inserted before target row `at` (at = rows appends); the override unchanged past MAX_POINTS. */
export function insertRow(base, edit, at) {
  if (!canInsertRow(base, edit)) return edit
  const next = clone(edit)
  const map = expandRows(edit?.rows ?? null, base.rows)
  map.splice(Math.max(0, Math.min(at, map.length)), 0, null)
  next.rows = compressRows(map, base.rows) ?? undefined
  next.cells = shiftRows(next.cells, at, 1)
  next.cats = shiftRows(next.cats, at, 1)
  return normalizeEdit(next, base)
}

export function removeRow(base, edit, at) {
  const next = clone(edit)
  const map = expandRows(edit?.rows ?? null, base.rows)
  if (map.length <= 1 || at < 0 || at >= map.length) return edit
  map.splice(at, 1)
  next.rows = compressRows(map, base.rows) ?? undefined
  next.cells = shiftRows(next.cells, at, -1)
  next.cats = shiftRows(next.cats, at, -1)
  return normalizeEdit(next, base)
}

/** The idx a new series gets: past every idx the chart and the override use. */
export function nextSeriesIdx(base, edit) {
  const used = [...base.series.map((series) => series.idx), ...(edit?.series ?? []).map((entry) => entry.idx)]
  return used.length ? Math.max(...used) + 1 : 0
}

/** Whether one more series fits (fitsPoints). */
export function canAddSeries(base, edit) {
  return fitsPoints(base, expandRows(edit?.rows ?? null, base.rows).length, seriesList(base, edit).length + 1)
}

/** A blank series right after `afterIdx`, copying its type, axis and colour rule. → { edit, idx }; idx null past MAX_POINTS. */
export function addSeries(base, edit, afterIdx) {
  if (!canAddSeries(base, edit)) return { edit, idx: null }
  const next = clone(edit)
  const list = seriesList(base, edit).map((entry) => ({ ...entry }))
  const position = list.findIndex((entry) => entry.idx === afterIdx)
  const after = list[position] ?? list[list.length - 1]
  const like = after ? (base.series.some((series) => series.idx === after.idx) ? after.idx : after.like) : undefined
  const idx = nextSeriesIdx(base, edit)
  list.splice(position >= 0 ? position + 1 : list.length, 0, like === undefined ? { idx } : { idx, like })
  next.series = list
  return { edit: normalizeEdit(next, base), idx }
}

export function removeSeries(base, edit, idx) {
  const list = seriesList(base, edit)
  const remaining = list.filter((entry) => entry.idx !== idx)
  const table = applyEdit(base, edit)
  const group = table.series.find((series) => series.idx === idx)?.group
  // A chart keeps at least one series in each of its chart types.
  if (remaining.length === list.length || !table.series.some((series) => series.idx !== idx && series.group === group)) return edit
  const next = clone(edit)
  next.series = remaining
  for (const row of Object.keys(next.cells ?? {})) delete next.cells[row][idx]
  if (next.names) delete next.names[idx]
  return normalizeEdit(next, base)
}

export function canRemoveSeries(base, edit, idx) {
  const table = applyEdit(base, edit)
  const group = table.series.find((series) => series.idx === idx)?.group
  return table.series.some((series) => series.idx !== idx && series.group === group)
}
